import {
  Component,
  inject,
  TemplateRef,
  viewChild,
  signal,
  AfterViewInit,
  computed
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';

import {
  DataDisplayComponent,
  ButtonComponent,
  DataContainerComponent,
  TableComponent,
  ColumnConfig,
  ColumnType,
  FilterOption,
  BadgeComponent
} from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable, switchMap, catchError, of } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OrganizationMemberListItem,
  OrganizationDetails
} from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { AuthService } from '../../../../../services/auth/auth.service';
import { UserRole } from '../../../../../services/auth/auth.role';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';

@Component({
  selector: 'app-organization-detail-page',
  imports: [
    AsyncPipe,
    CommonModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    TableComponent,
    ButtonComponent,
    BadgeComponent,
    ProtectedContentComponent
  ],
  templateUrl: './organization-detail-page.component.html',
  styleUrl: './organization-detail-page.component.scss'
})
export class OrganizationDetailPageComponent implements AfterViewInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  requiredRoles = this.navigationService.getAccessRoles('admin/organizations');

  /** Whether the user's organization is pending (blocks edit/add actions) */
  isOrganizationPending = signal(false);

  // Computed: hide back button if user is only OrganizationAdmin
  showBackButton = computed(() => {
    const userRoles = this.authService.userRoles$.value;
    const isOnlyOrgAdmin =
      userRoles.includes(UserRole.OrganizationAdmin) &&
      !userRoles.includes(UserRole.DataspaceAuthority);
    return !isOnlyOrgAdmin;
  });

  /** Whether the current user can edit organizations (role check + org not pending) */
  canEditOrganizations = this.navigationService
    .getAccessRoles('admin/organizations/edit')
    .some((role) => this.authService.userRoles$.value.includes(role));

  /** Whether the current user can invite new members (role check + org not pending) */
  canAddMembers = this.navigationService
    .getAccessRoles('admin/organizations/members/add')
    .some((role) => this.authService.userRoles$.value.includes(role));

  actionsTemplateRef = viewChild<TemplateRef<OrganizationMemberListItem>>('columnActions');
  rolesTemplateRef = viewChild<TemplateRef<OrganizationMemberListItem>>('columnRoles');
  private actionsTemplate = signal<TemplateRef<OrganizationMemberListItem> | undefined>(undefined);
  private rolesTemplate = signal<TemplateRef<OrganizationMemberListItem> | undefined>(undefined);
  private templatesReady = signal(false);

  constructor() {
    this.organizationService.isOrganizationPending$().subscribe((pending) => {
      this.isOrganizationPending.set(pending);
    });
  }

  approvalForm = this.fb.group({
    reasoning: ['', Validators.required]
  });

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  data$: Observable<OrganizationDetails> = this.id$.pipe(
    switchMap((id) => this.organizationService.getDetailsById(id)),
    catchError(() => {
      this.notificationService.showError('organization.detail.loadingError', 5000);
      return of();
    })
  );

  organisationDetails$ = this.data$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('organization.control.bpnl.label'),
          value: [dto.bpnl]
        },
        {
          label: 'DID',
          value: [dto.did || '-']
        },
        {
          label: this.translocoService.translate('organization.control.activityState.label'),
          value: [
            this.translocoService.translate(
              'organization.control.activityState.options.' + dto.activityState
            ) ||
              dto.activityState ||
              '-'
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approval.summary.foundingDate.label'),
          value: [
            dto.foundingDate
              ? new Date(dto.foundingDate).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              : ''
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.address.label'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.commercialRegisterNumber.label'),
          value: [dto.commercialRegisterNumber, dto.commercialRegisterCourt]
        },
        {
          label: this.translocoService.translate('approval.summary.website.label'),
          value: [dto.website]
        },
        {
          label: this.translocoService.translate('approval.summary.taxIdNumber.label'),
          value: [dto.taxIdNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.sector.label'),
          value: [dto.sector]
        },
        {
          label: this.translocoService.translate('approval.summary.description.label'),
          value: [dto.description]
        }
      ];
    })
  );

  member$: Observable<OrganizationMemberListItem[]> = this.id$.pipe(
    switchMap((id) => this.organizationService.findMembersOfOrganization(id))
  );

  columnConfig$: Observable<ColumnConfig<OrganizationMemberListItem>[]> = this.translocoService
    .selectTranslate([])
    .pipe(
      switchMap(() => {
        return new Observable<ColumnConfig<OrganizationMemberListItem>[]>((observer) => {
          const checkTemplates = () => {
            if (this.templatesReady()) {
              observer.next(this.buildColumnConfig());
              observer.complete();
            } else {
              setTimeout(checkTemplates, 50);
            }
          };
          checkTemplates();
        });
      })
    );

  private buildColumnConfig(): ColumnConfig<OrganizationMemberListItem>[] {
    const actionsTmpl = this.actionsTemplate();
    const rolesTmpl = this.rolesTemplate();
    return [
      {
        name: 'firstName',
        headerLabel: this.translocoService.translate(
          'organization.members.control.firstName.label'
        ),
        columnType: ColumnType.PLAIN
      },
      {
        name: 'lastName',
        headerLabel: this.translocoService.translate('organization.members.control.lastName.label'),
        columnType: ColumnType.PLAIN,
        filterable: true,
        sortable: true
      },
      {
        name: 'position',
        headerLabel: this.translocoService.translate('organization.members.control.position.label'),
        columnType: ColumnType.PLAIN,
        filterable: true,
        sortable: true
      },
      {
        name: 'roles',
        headerLabel: this.translocoService.translate('organization.members.control.roles.label'),
        columnType: ColumnType.TEMPLATE,
        template: rolesTmpl as any,
        filterable: true,
        filterOptions: [
          {
            value: 'ORGANIZATION_USER',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_user'
            )
          },
          {
            value: 'ORGANIZATION_REPRESENTATIVE',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_representative'
            )
          },
          {
            value: 'ORGANIZATION_ADMIN',
            label: this.translocoService.translate(
              'organization.member.roles.labels.organization_admin'
            )
          },
          {
            value: 'DATASPACE_AUTHORITY',
            label: this.translocoService.translate(
              'organization.member.roles.labels.dataspace_authority'
            )
          }
        ] as FilterOption[]
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.action.label'),
        template: actionsTmpl as any
      }
    ];
  }

  backToList() {
    void this.router.navigate(['admin', 'organizations']);
  }

  ngAfterViewInit(): void {
    this.actionsTemplate.set(this.actionsTemplateRef());
    this.rolesTemplate.set(this.rolesTemplateRef());
    this.templatesReady.set(true);
  }

  edit() {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'edit']);
    });
  }

  viewMember(member: OrganizationMemberListItem) {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'members', member.id]);
    });
  }

  editMember(member: OrganizationMemberListItem) {
    this.id$.subscribe((id) => {
      void this.router.navigate([
        'admin',
        'organizations',
        'details',
        id,
        'members',
        member.id,
        'edit'
      ]);
    });
  }

  addMember() {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id, 'members', 'add']);
    });
  }

  getTyped(row: unknown) {
    return row as OrganizationMemberListItem;
  }
}
